import { isWord, type PageElement } from "./types";

/**
 * Where everything sits on a page: what the editor needs to find a word by id,
 * walk the words in order, and group them into paragraphs.
 *
 * None of it changes when a word is marked. Marking replaces a word object but
 * never adds, removes or moves one, and yet the editor rebuilt all of this on
 * every mark — a map of every word, a list and a set of every id — which on a
 * 4,600-word article was the largest share of what a mark cost to render once
 * the reducer itself was cheap.
 */
export interface PageLayout {
  /** Every word's id, in reading order. */
  wordIds: readonly string[];
  /** Each word's index in the page array. */
  indexOf: ReadonlyMap<string, number>;
  /** Each paragraph as a `[start, end)` range of the page array, breaks excluded. */
  paragraphs: ReadonlyArray<readonly [number, number]>;
  /** The paragraph each page index falls in; -1 for a break. */
  paragraphAt: readonly number[];
}

export const layoutOf = (page: readonly PageElement[]): PageLayout => {
  const wordIds: string[] = [];
  const indexOf = new Map<string, number>();
  const paragraphs: Array<readonly [number, number]> = [];
  const paragraphAt: number[] = [];
  let start = -1;

  page.forEach((element, index) => {
    if (isWord(element)) {
      if (start < 0) start = index;
      wordIds.push(element.id);
      indexOf.set(element.id, index);
      paragraphAt.push(paragraphs.length);
      return;
    }
    // `textToPage` closes every paragraph with a break and drops empty ones, so
    // a break with no words before it would only be a stray, zero-height block.
    if (start >= 0) paragraphs.push([start, index]);
    start = -1;
    paragraphAt.push(-1);
  });
  if (start >= 0) paragraphs.push([start, page.length]);

  return { wordIds, indexOf, paragraphs, paragraphAt };
};

/** True when two pages hold the same elements in the same places, marked or not. */
export const sameStructure = (
  a: readonly PageElement[],
  b: readonly PageElement[],
): boolean =>
  a.length === b.length &&
  a.every(
    (element, index) =>
      element === b[index] ||
      (element.id === b[index].id && element.type === b[index].type),
  );

/**
 * A `layoutOf` that hands back the same layout for as long as the page keeps its
 * structure. Checking that is a pass of identity comparisons, which is far
 * cheaper than building the maps again; and handing back the same object keeps
 * everything memoised on the layout from recomputing.
 */
export const memoizeLayout = (): ((page: readonly PageElement[]) => PageLayout) => {
  let last: { page: readonly PageElement[]; layout: PageLayout } | undefined;
  return (page) => {
    if (!last || (last.page !== page && !sameStructure(last.page, page))) {
      last = { page, layout: layoutOf(page) };
    } else {
      last.page = page;
    }
    return last.layout;
  };
};
